/**
 * Street View imagery coverage checks. Google only has panoramas along public
 * roads — none over most of the Space Coast bases — so every Street View entry
 * point asks here before opening, instead of rendering a blank panorama.
 */

import { importLibrary } from '@googlemaps/js-api-loader';

/** Radius (m) for the zoom-dive auto-open — only hijack the dive when imagery is underfoot. */
export const AUTO_COVERAGE_RADIUS_M = 75;

/** Radius (m) for explicit opens (selection card) — snap to the nearest pano the user asked for. */
export const MANUAL_COVERAGE_RADIUS_M = 250;

/** A found panorama: its id and the snapped imagery location. */
export interface CoverageHit {
  panoId: string;
  lat: number;
  lon: number;
}

/** Looks up the nearest outdoor panorama within radius; resolves null when none exists. */
export type PanoFetcher = (lat: number, lon: number, radius: number) => Promise<CoverageHit | null>;

/** How long a lookup (hit or miss) stays cached. */
const TTL_MS = 300_000;

/** Max cached lookups before the oldest entry is evicted. */
const MAX_ENTRIES = 256;

/** Coordinate rounding for cache keys (~110 m buckets), so the dive poll never spams the API. */
const KEY_DECIMALS = 3;

/** Cache key bucketing nearby lookups of the same radius together. */
export function coverageKey(lat: number, lon: number, radius: number): string {
  return `${lat.toFixed(KEY_DECIMALS)},${lon.toFixed(KEY_DECIMALS)}:${radius}`;
}

interface CacheEntry {
  at: number;
  value: Promise<CoverageHit | null>;
}

/** A memoizing coverage checker (hits, misses, and in-flight lookups all dedupe). */
export interface CoverageChecker {
  check: (lat: number, lon: number, radius: number) => Promise<CoverageHit | null>;
}

/** Build a checker around a fetcher (injectable for tests). */
export function createCoverageChecker(fetcher: PanoFetcher): CoverageChecker {
  const cache = new Map<string, CacheEntry>();

  return {
    check(lat: number, lon: number, radius: number): Promise<CoverageHit | null> {
      const key = coverageKey(lat, lon, radius);
      const cached = cache.get(key);
      if (cached && Date.now() - cached.at < TTL_MS) return cached.value;

      // Transport failures must not become 5-minute "no imagery" verdicts:
      // evict on rejection so the next tick retries, resolving null just once.
      const value: Promise<CoverageHit | null> = fetcher(lat, lon, radius).catch(() => {
        const entry = cache.get(key);
        if (entry && entry.value === value) cache.delete(key);
        return null;
      });
      cache.delete(key);
      cache.set(key, { at: Date.now(), value });
      if (cache.size > MAX_ENTRIES) {
        const oldest = cache.keys().next().value;
        if (oldest !== undefined) cache.delete(oldest);
      }
      return value;
    },
  };
}

/** Real fetcher backed by the Maps JS StreetViewService. */
async function fetchPanorama(lat: number, lon: number, radius: number): Promise<CoverageHit | null> {
  await importLibrary('streetView');
  const service = new google.maps.StreetViewService();
  try {
    const { data } = await service.getPanorama({
      location: { lat, lng: lon },
      radius,
      source: google.maps.StreetViewSource.OUTDOOR,
      preference: google.maps.StreetViewPreference.NEAREST,
    });
    const panoId = data.location?.pano;
    const position = data.location?.latLng;
    if (!panoId || !position) return null;
    return { panoId, lat: position.lat(), lon: position.lng() };
  } catch (err) {
    if (isZeroResults(err)) return null; // genuinely no imagery near this point
    throw err; // transport/quota errors propagate so they are never cached as misses
  }
}

/** Whether a getPanorama rejection means "no imagery" rather than a transient failure. */
function isZeroResults(err: unknown): boolean {
  return typeof err === 'object' && err !== null && (err as { code?: unknown }).code === 'ZERO_RESULTS';
}

const liveChecker = createCoverageChecker(fetchPanorama);

/** Nearest outdoor panorama within radius of a point, or null — cached ~5 min per area. */
export function checkStreetViewCoverage(
  lat: number,
  lon: number,
  radius: number,
): Promise<CoverageHit | null> {
  return liveChecker.check(lat, lon, radius);
}
